import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useDispatch } from 'react-redux';
import { useAuth } from '@bb/session-manager';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import { setErrMsg, setSuccessMsg } from '../../redux/actions/snackbarActions';
import { deleteAccount } from '../../requests/subscription';
import { SUBSCRIPTION_UPGRADE_ROUTE } from '../../routes.config';

export function TrialExpiredInterstitial() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { signOut } = useAuth();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [countdown, setCountdown] = useState(null);

  useEffect(() => {
    if (countdown === null) return;
    if (countdown === 0) { signOut(); return; }
    const t = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown, signOut]);

  const handleUpgrade = useCallback(() => {
    navigate({ to: SUBSCRIPTION_UPGRADE_ROUTE });
  }, [navigate]);

  const handleDeleteConfirm = useCallback(async () => {
    setDeleting(true);
    try {
      await deleteAccount();
      dispatch(setSuccessMsg('Your account has been marked for deletion. Data will be removed within 30 days.'));
      setDeleteDialogOpen(false);
      setCountdown(30);
    } catch {
      dispatch(setErrMsg('Failed to process deletion request. Please contact support@blackboiler.com.'));
    } finally {
      setDeleting(false);
    }
  }, [dispatch]);

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        backdropFilter: 'blur(4px)',
      }}
    >
      <Box
        sx={{
          backgroundColor: 'white',
          borderRadius: 3,
          padding: 6,
          maxWidth: 480,
          width: '90%',
          textAlign: 'center',
          boxShadow: '0 24px 48px rgba(0,0,0,0.3)',
        }}
      >
        {countdown !== null ? (
          <>
            <Typography variant="h5" fontWeight={700} gutterBottom sx={{ color: '#1a1a1a' }}>
              Account deletion requested
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Your account has been marked for deletion. Data will be removed within 30 days.
            </Typography>
            <Typography variant="h6" sx={{ color: 'text.secondary' }}>
              Signing you out in {countdown}…
            </Typography>
          </>
        ) : (
          <>
            <Typography variant="h4" fontWeight={700} gutterBottom sx={{ color: '#1a1a1a' }}>
              Your trial has ended
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              Your 7-day free trial has expired. Upgrade to continue using BlackBoiler and keep your data.
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                onClick={handleUpgrade}
                sx={{ py: 1.5, fontSize: '1rem', fontWeight: 600 }}
              >
                Upgrade Now
              </Button>
              <Button
                variant="text"
                size="small"
                onClick={() => setDeleteDialogOpen(true)}
                sx={{ color: 'text.secondary', '&:hover': { color: 'error.main' } }}
              >
                Delete my data
              </Button>
            </Box>
          </>
        )}
      </Box>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete your account?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your data will be permanently deleted within 30 days. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
          >
            {deleting ? 'Processing...' : 'Delete my data'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
